import { useState } from "react";

/* ---------------- COUNTER INPUT ---------------- */
const CounterInput = ({ label, value, min, max, onChange, error }) => (
  <div>
    <label className="text-white text-sm block mb-1">{label}</label>

    <div className="flex items-center gap-2 border border-white rounded-md px-3 py-2">
      <button
        type="button"
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
        className="text-xl text-white disabled:opacity-40"
      >
        −
      </button>

      <input
        type="text"
        readOnly
        value={value}
        className="w-full bg-transparent text-center text-white outline-none pointer-events-none"
      />

      <button
        type="button"
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
        className="text-xl text-white disabled:opacity-40"
      >
        +
      </button>
    </div>

    {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
  </div>
);

/* ---------------- MAIN FORM ---------------- */
export default function BookingForm() {

  const [submitted, setSubmitted] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+[1-9]\d{7,14}$/;

  const initialFormState = {
  checkIn: "",
  checkOut: "",
  adult: 2,
  child: 0,
  email: "",
  phone: "",
};

const [form, setForm] = useState(initialFormState);
const [errors, setErrors] = useState({});
const [showSuccess, setShowSuccess] = useState(false)
const [loading, setLoading] = useState(false)

  /* ---------------- VALIDATION ---------------- */
  const validateForm = (data) => {
    const errs = {};

    if (!data.checkIn) errs.checkIn = "Check-in required";
    if (!data.checkOut) errs.checkOut = "Check-out required";

    if (!emailRegex.test(data.email))
      errs.email = "Invalid email address";

    if (!phoneRegex.test(data.phone))
      errs.phone = "Invalid phone number with country code";

    if (data.adult < 1 || data.adult > 10)
      errs.adult = "Adults must be between 1 and 10";

    if (data.child < 0 || data.child > 10)
      errs.child = "Children must be between 0 and 10";

    return errs;
  };

  /* ---------------- UPDATE HANDLER ---------------- */
const updateForm = (key, value) => {

  let updated = { ...form, [key]: value };

  if (key === "checkIn") {
    updated.checkOut = "";
  }

  setForm(updated);
};


  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length !== 0) return;

    try {
      setLoading(true); // Disable button
      const result = await sendBookingToBackend(form);

      if (result.success) {
        openWhatsApp(form);
        setShowSuccess(true);
        setForm(initialFormState); // Reset form
        setErrors({});
        setSubmitted(false);

      } else {
        alert("Failed to send booking: " + result.message);
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting form. Please try again.");
    } finally {
      setLoading(false); // Re-enable button
    }
  };

  // Backend API call
  const sendBookingToBackend = async (formData) => {
    console.log(formData)
    const response = await fetch(
      "https://api.orcaminds.com/bookingRequest",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      }
    );

    const result = await response.json();

    // Handle HTTP errors
    if (!response.ok) {
      throw new Error(result.message || "Server returned an error");
    }

    return result;
  };

  const buildWhatsAppMessage = (form) => {
  return encodeURIComponent(
    `Hello 👋,

I would like to make a booking:

Check-in Date: ${form.checkInDate}
Check-out Date: ${form.checkOutDate}
Adults: ${form.adults}
Children: ${form.children}

Contact Info:
Phone: ${form.phone}
Email: ${form.email}

Thank you!`
  );
};

const openWhatsApp = (form) => {
  const phoneNumber = "+918489245920"; 
  const message = buildWhatsAppMessage(form);

  // Universal URL
  const webUrl = `https://wa.me/${phoneNumber}?text=${message}`;
  const appUrl = `whatsapp://send?phone=${phoneNumber}&text=${message}`;

  // Detect mobile
  const isMobile =
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  if (isMobile) {
    
    window.location.href = appUrl;
    setTimeout(() => {
      window.open(webUrl, "_blank");
    }, 1500);
  } else {
    window.open(webUrl, "_blank");
  }
};


  /* ---------------- UI ---------------- */
  return (
    <div className="relative mt-20 w-full max-w-xl bg-[#0b2c44]/80 rounded-xl p-6 sm:p-8 text-white mb-20">
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >


{showSuccess && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div className="bg-white w-[90%] max-w-sm rounded-2xl p-6 text-center shadow-2xl animate-[scaleIn_0.25s_ease-out]">
      
      {/* Success Icon */}
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <svg
          className="h-8 w-8 text-green-600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      {/* Title */}
      <h2 className="text-lg font-bold text-gray-800">
        Booking Request Details Sent 
      </h2>

      {/* Message */}
      <p className="mt-2 text-sm font-semibold text-gray-600">
        Thanks for reaching out! Our team will contact you shortly.
      </p>

      {/* Button */}
      <button
        onClick={() => setShowSuccess(false)}
        className="mt-6 w-full rounded-lg bg-sky-500 py-2.5 text-white font-medium transition hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-400"
      >
        Got it
      </button>
    </div>
  </div>
)}


        {/* CHECK IN */}
        <div>
          <label className="block text-xs font-semibold mb-1 uppercase">
            Check In
          </label>
          <input
            type="date"
            min={today}
            value={form.checkIn}
            onChange={(e) =>
              updateForm("checkIn", e.target.value)
            }
            className="w-full bg-transparent border border-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/60"
          />
          {submitted && errors.checkIn && (
            <p className="text-red-400 text-xs">{errors.checkIn}</p>
          )}
        </div>

        {/* CHECK OUT */}
        <div>
          <label className="block text-xs font-semibold mb-1 uppercase">
            Check Out
          </label>
          <input
            type="date"
            min={form.checkIn || today}
            value={form.checkOut}
            onChange={(e) =>
              updateForm("checkOut", e.target.value)
            }
            className="w-full bg-transparent border border-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/60"
          />
          {submitted && errors.checkOut && (
            <p className="text-red-400 text-xs">{errors.checkOut}</p>
          )}
        </div>

        {/* ADULT */}
        <CounterInput
          label="ADULT"
          min={1}
          max={10}
          value={form.adult}
          onChange={(val) => updateForm("adult", val)}
          error={errors.adult}
        />

        {/* CHILD */}
        <CounterInput
          label="CHILD"
          min={0}
          max={10}
          value={form.child}
          onChange={(val) => updateForm("child", val)}
          error={errors.child}
        />

        {/* EMAIL */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold mb-1 uppercase">
            Email
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) =>
              updateForm("email", e.target.value)
            }
            placeholder="Email"
            className="w-full bg-transparent border border-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/60"
          />
          {submitted && errors.email && (
            <p className="text-red-400 text-xs">{errors.email}</p>
          )}
        </div>

        {/* PHONE */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold mb-1 uppercase">
            Phone
          </label>
          <input
            type="tel"
            placeholder="+91XXXXXXXXXX"
            value={form.phone}
            onChange={(e) =>
              updateForm("phone", e.target.value)
            }
            className="w-full bg-transparent border border-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/60"
          />
          {submitted && errors.phone && (
            <p className="text-red-400 text-xs">{errors.phone}</p>
          )}
        </div>

        {/* BUTTON */}
        <div className="sm:col-span-2 flex justify-end pt-2">
      <button
        type="submit"
        disabled={loading}
        className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-6 py-2 rounded-md transition"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          padding: "10px 20px",
          cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading && (
          <span
            style={{
              border: "2px solid #f3f3f3",
              borderTop: "2px solid #3498db",
              borderRadius: "50%",
              width: "16px",
              height: "16px",
              animation: "spin 1s linear infinite",
              display: "inline-block",
            }}
          />
        )}
            CHECK AVAILABILITY
          </button>
        </div>
      </form>
    </div>
  );
}
